import { Divider, styled, Typography } from '@mui/material';
import { skipToken } from '@tanstack/react-query';
import { createIncomeOrExpenseSuccessRoute } from 'adminConstants/routes';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import AllocationsPreview from 'components/new/AllocationsPreview';
import FullHeightView from 'components/new/FullHeightView';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import ReviewPayments from 'components/ReviewPayments';
import { PaymentActivity } from 'further-types/lp/bank-reconciliation';
import {
  IncomeOrExpenseApportionmentMode,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { Column, Row } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { useDisclosure } from 'further-ui/hooks';
import {
  IncomeAndExpensesLabels,
  IncomeOrExpenseStateLabels,
} from 'further-ui/labels';
import { numberToCurrencyString, stringToColor } from 'further-ui/utils';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { compact } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import ApportionmentSummary from '../../shared/ApportionmentSummary';
import { FormValues } from '../FormWrapper';
import ReviewAccountTransactions from './ReviewAccountTransactions';

const Dot = styled(ColorIndicator)(({ theme }) => ({
  margin: theme.spacing(theme.spacingSizes.xs),
}));

const shouldHidePayments = (
  payments: Array<{
    activity: PaymentActivity;
    amountTcy: number;
  }>,
  amountTcy: number,
) => {
  return (
    payments.find((payment) => payment.activity === PaymentActivity.Created)
      ?.amountTcy !== amountTcy
  );
};

const getTransactionIdToDisplay = (
  transactions?: Array<{ reconciledAmountTcy?: number; id?: string }>,
) => {
  const reconciledTransaction = transactions?.find(
    (t) => t.reconciledAmountTcy,
  );
  return reconciledTransaction?.id ?? transactions?.[0]?.id ?? '';
};

type Props = {
  id?: string;
  isSwitchMode?: boolean;
  onPreviousStep: () => void;
  vehicle: {
    _id: string;
    vehicleName: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
};

const StepReviewAndConfirm: React.FC<Props> = ({
  id,
  isSwitchMode,
  onPreviousStep,
  vehicle,
}) => {
  const notification = useNotification();
  const history = useHistory();
  const { getValues, handleSubmit } = useFormContext<FormValues>();

  const { hasEqualisationInDraftState } = useEqualisations(vehicle._id);
  const confirmDialog = useDisclosure();

  const values = getValues();
  const apportionmentModeIsMultiple =
    values.apportionmentMode === IncomeOrExpenseApportionmentMode.Multiple;

  const {
    data: apportionmentPeriodsData,
    isLoading: apportionmentPeriodsLoading,
  } = api.incomeAndExpenses.apportionmentPeriods.useQuery(
    apportionmentModeIsMultiple
      ? {
          vehicleId: values.vehicleId,
          apportionmentBasis: values.apportionmentBasis,
          apportionmentStartDate: values.startDate,
          apportionmentEndDate: values.endDate,
          amountIncVatTcy: values.amountIncVatTcy,
          amountExVatTcy: values.amountExVatTcy,
          currency: values.currencyCode,
          allocations: values.allocations,
        }
      : skipToken,
  );

  const mutationOptions = useHandleTrpcMutation()({
    onSuccess: () => {
      history.push(createIncomeOrExpenseSuccessRoute(values.category));
    },
  });
  const {
    mutate: createIncomeOrExpense,
    isPending: createIncomeOrExpensePending,
  } = api.incomeAndExpenses.create.useMutation(mutationOptions);

  const {
    mutate: updateIncomeOrExpense,
    isPending: updateIncomeOrExpensePending,
  } = api.incomeAndExpenses.update.useMutation(mutationOptions);

  const {
    mutate: switchIncomeOrExpense,
    isPending: switchIncomeOrExpensePending,
  } = api.incomeAndExpenses.switch.useMutation(mutationOptions);

  const onSubmit = () => {
    const data = getValues();
    if (isSwitchMode && id) {
      switchIncomeOrExpense({
        vehicleId: data.vehicleId,
        id,
        switchDate: data.switchDate!,
        counterparty: data.counterparty,
        incomeOrExpenseType: data.incomeOrExpenseType,
        allocations: data.allocations,
        fundingSource: data.fundingSource,
        vatRate: data.vatRate,
        amountExVatTcy: data.amountExVatTcy,
        description: data.description,
        invoiceFile: data.invoiceFile,
        apportionmentPeriods: data.apportionmentPeriods,
      });
    } else if (id) {
      updateIncomeOrExpense({
        ...data,
        id,
      });
    } else {
      createIncomeOrExpense(data);
    }
  };

  const isPending =
    createIncomeOrExpensePending ||
    updateIncomeOrExpensePending ||
    switchIncomeOrExpensePending;

  if (apportionmentModeIsMultiple && apportionmentPeriodsLoading) {
    return <PageLoader />;
  }

  const allocationsLabel =
    values.category === IncomeOrExpenseType.Income ? 'Income' : 'Expense';

  const currentActionIds = compact(
    values.apportionmentPeriods.map((period) => period.id),
  );

  const currencyRate = values.apportionmentPeriods[0]?.currencyRate;
  const effectiveCurrencyRate =
    currencyRate?.overrideRate ?? currencyRate?.fetchedRate;

  return (
    <FullHeightView
      actionBar={
        <Row justify="space-between" centered>
          <Button
            variant="outlined"
            onClick={onPreviousStep}
            disabled={isPending}
            size="large"
          >
            Previous step
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={
              hasEqualisationInDraftState
                ? confirmDialog.onOpen
                : handleSubmit(onSubmit, () =>
                    notification.error('There is an error in the form.'),
                  )
            }
            size="large"
            loading={isPending}
          >
            Save and approve
          </Button>
        </Row>
      }
    >
      <Column spacing="lg">
        <StaticField label="Vehicle">{vehicle?.vehicleName}</StaticField>
        <StaticField label="Counterparty">
          {values.counterparty?.name}
        </StaticField>
        <StaticField label="Type">
          {IncomeAndExpensesLabels[values.incomeOrExpenseType] ??
            values.incomeOrExpenseType}
        </StaticField>
        <StaticField label="State">
          {IncomeOrExpenseStateLabels[values.incomeOrExpenseState]}
        </StaticField>
        <StaticField label="Amount">
          {numberToCurrencyString(values.amountIncVatTcy, {
            currency: values.currencyCode,
          })}
        </StaticField>
        <StaticField label="Ledger date">
          {values.startDate.toReadable()}
        </StaticField>
        {apportionmentModeIsMultiple && !!apportionmentPeriodsData && (
          <ApportionmentSummary
            apportionmentPeriods={apportionmentPeriodsData}
            currency={values.currencyCode}
          />
        )}
        <Divider />
        <Column spacing="sm">
          <Typography variant="h5">
            Review {apportionmentModeIsMultiple ? 'apportionment ' : ' '}
            allocations
          </Typography>
          <AllocationsPreview
            vehicleId={vehicle._id}
            allocations={values.allocations}
            baseCurrency={vehicle.baseCurrency}
            currency={values.currencyCode}
            allocatedActionLabel={allocationsLabel}
            apportionmentPeriods={apportionmentPeriodsData}
            date={values.startDate}
            currentActionIds={currentActionIds}
            showPaymentData={
              !!id &&
              values.fundingSource !== FundingSource.GeneralCapitalOnAccounts
            }
            currencyRate={effectiveCurrencyRate}
          />
        </Column>
        <Divider />

        <Typography variant="h5">
          Review {apportionmentModeIsMultiple ? 'apportionment ' : ' '}account
          transactions
        </Typography>

        {apportionmentModeIsMultiple ? (
          apportionmentPeriodsData?.map((period, index) => (
            <Column key={index}>
              <ReviewAccountTransactions
                amountIncVatTcy={period.amountApportionedIncVatTcy}
                amountExVatTcy={period.amountApportionedExVatTcy}
                currencyCode={values.currencyCode}
                vehicle={vehicle}
                subtitle={period.periodStartDate.toReadable()}
                periodStartDate={period.periodStartDate}
                periodIndex={index}
                isSwitchMode={isSwitchMode}
              />
              <ErrorBoundary>
                <ReviewPayments
                  transactionId={getTransactionIdToDisplay(
                    values.apportionmentPeriods[index]?.transactions,
                  )}
                  vehicleId={vehicle._id}
                  currencyCode={values.currencyCode}
                  amountTcy={period.amountApportionedIncVatTcy}
                  hidePayments={shouldHidePayments}
                  title={
                    <Row centered spacing="xs">
                      <Dot color={stringToColor('Payment record')} />
                      <Typography variant="h6">Payment record</Typography>
                    </Row>
                  }
                />
              </ErrorBoundary>
            </Column>
          ))
        ) : (
          <ReviewAccountTransactions
            amountIncVatTcy={values.amountIncVatTcy}
            amountExVatTcy={values.amountExVatTcy}
            currencyCode={values.currencyCode}
            vehicle={vehicle}
            periodStartDate={values.startDate}
            periodIndex={0}
            isSwitchMode={isSwitchMode}
          />
        )}
      </Column>

      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={handleSubmit(onSubmit, () =>
            notification.error('There is an error in the form.'),
          )}
          loading={createIncomeOrExpensePending || updateIncomeOrExpensePending}
        />
      )}
    </FullHeightView>
  );
};

export default StepReviewAndConfirm;
